import React, { useState, useEffect, useRef } from 'react';
import { Resident } from '../types';
import api from '../utils/api';

interface ExternalCarePlan {
  id: number;
  residentId: number;
  fileName: string;
  fileType: string;
  fileUrl: string;
  description?: string;
  uploadedBy?: { id: number; name: string };
  createdAt: string;
}

interface ResidentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  resident: Resident | null;
}

interface ProfileData {
  assignedStaff: any[];
  medicalRecords: {
    medications: any[];
    allergies: any[];
    conditions: any[];
    vitalSigns: any[];
  };
  recentNotes: any[];
  upcomingAppointments: any[];
  recentIncidents: any[];
  activeSupportPlans: any[];
  financeAccount: any | null;
  recentTransactions: any[];
}

const ResidentDetailsModal: React.FC<ResidentDetailsModalProps> = ({
  isOpen,
  onClose,
  resident
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [externalCarePlans, setExternalCarePlans] = useState<ExternalCarePlan[]>([]);
  const [ecpLoading, setEcpLoading] = useState(false);
  const [ecpError, setEcpError] = useState<string | null>(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && resident) {
      fetchProfileData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resident]);

  const fetchProfileData = async () => {
    if (!resident) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/residents/${resident.id}/profile`);
      if (response.data.success) {
        setProfileData(response.data.data);
      }
    } catch (error: any) {
      console.error('Error fetching profile data:', error);
      setError(error.response?.data?.message || 'Failed to load profile data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && resident && activeTab === 'external-care-plans') {
      fetchExternalCarePlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, resident, activeTab]);

  const fetchExternalCarePlans = async () => {
    if (!resident) return;
    setEcpLoading(true);
    setEcpError(null);
    try {
      const response = await api.get(`/external-care-plans/resident/${resident.id}`);
      if (response.data.success) {
        setExternalCarePlans(response.data.data || []);
      }
    } catch (err: any) {
      console.error('Error fetching external care plans:', err);
      setEcpError(err.response?.data?.message || 'Failed to load external care plans');
    } finally {
      setEcpLoading(false);
    }
  };

  const handleUploadExternalCarePlan = async () => {
    if (!resident || !fileInputRef.current?.files?.[0]) return;

    const file = fileInputRef.current.files[0];
    setUploading(true);
    setEcpError(null);

    try {
      // Step 1: Get presigned upload URL
      const uploadResponse = await api.post('/objects/upload', {
        fileName: file.name,
        fileType: file.type,
      });

      if (!uploadResponse.data.success) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, key } = uploadResponse.data.data;

      // Step 2: Upload file to presigned URL
      await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      // Step 3: Save metadata
      await api.post('/external-care-plans', {
        residentId: resident.id,
        fileName: file.name,
        fileType: file.type,
        fileKey: key,
        description: uploadDescription,
      });

      // Reset form and refresh list
      setShowUploadForm(false);
      setUploadDescription('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchExternalCarePlans();
    } catch (err: any) {
      console.error('Error uploading external care plan:', err);
      setEcpError(err.response?.data?.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteExternalCarePlan = async (planId: number) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/external-care-plans/${planId}`);
      fetchExternalCarePlans();
    } catch (err: any) {
      console.error('Error deleting external care plan:', err);
      setEcpError(err.response?.data?.message || 'Failed to delete document');
    }
  };

  const getFileTypeIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return '📄';
    if (fileType.includes('image')) return '🖼️';
    if (fileType.includes('word') || fileType.includes('document')) return '📝';
    if (fileType.includes('spreadsheet') || fileType.includes('excel')) return '📊';
    return '📎';
  };

  if (!isOpen || !resident) return null;

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'medical', label: 'Medical Records' },
    { id: 'staff', label: 'Assigned Staff' },
    { id: 'activities', label: 'Recent Activities' },
    { id: 'external-care-plans', label: 'External Care Plans' },
    { id: 'finance', label: 'Finance' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-5/6 lg:w-4/5 xl:w-3/4 shadow-lg card-modal bg-white mb-10">
        {/* Header */}
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <img
              className="h-16 w-16 rounded-full object-cover"
              src={resident.photo || '/images/profiles/placeholder-resident.jpg'}
              alt={resident.name}
            />
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{resident.name}</h3>
              <p className="text-sm text-gray-500">
                Age {resident.age}
                {resident.dateOfBirth && ` • DOB: ${formatDate(resident.dateOfBirth)}`}
                {' '}• ID: {resident.id}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Tabs */}
        <div className="mt-4 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="mt-6 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-500 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-gray-700 mb-4">{error}</p>
              <button
                onClick={fetchProfileData}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Medical Diagnosis</h5>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{resident.diagnosis}</p>
                    </div>

                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Status</h5>
                      <span className={`inline-flex px-3 py-1 text-sm leading-5 font-semibold rounded-full ${
                        resident.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {resident.isActive ? 'Active' : 'Discharged'}
                      </span>
                    </div>

                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Admission Date</h5>
                      <p className="text-gray-700">{formatDate(resident.admissionDate)}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h5 className="text-lg font-semibold text-gray-900 mb-3">Emergency Contact</h5>
                      <div className="bg-red-50 border border-red-100 p-4 rounded-lg space-y-2">
                        <div>
                          <span className="text-sm font-medium text-gray-900">Name: </span>
                          <span className="text-gray-700">{resident.emergencyContact?.name || 'Not provided'}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">Phone: </span>
                          <span className="text-gray-700">{resident.emergencyContact?.phone || 'Not provided'}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900">Relationship: </span>
                          <span className="text-gray-700">{resident.emergencyContact?.relationship || 'Not provided'}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Record Information</h5>
                      <div className="text-sm text-gray-600 space-y-1 bg-gray-50 p-3 rounded-lg">
                        <p>Created: {formatDate(resident.createdAt)}</p>
                        <p>Last Updated: {formatDate(resident.updatedAt)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Medical Records Tab */}
              {activeTab === 'medical' && profileData && (
                <div className="space-y-4">
                  {/* Medications */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Current Medications</h4>
                    {profileData.medicalRecords.medications.length > 0 ? (
                      <div className="space-y-2">
                        {profileData.medicalRecords.medications.map((med: any) => (
                          <div key={med.id} className="bg-blue-50 border border-blue-100 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{med.name}</p>
                                <p className="text-sm text-gray-600">{med.dosage} - {med.frequency}</p>
                                {med.instructions && <p className="text-sm text-gray-500 mt-1">{med.instructions}</p>}
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                med.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {med.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No medications recorded</p>
                    )}
                  </div>

                  {/* Allergies */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Allergies</h4>
                    {profileData.medicalRecords.allergies.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {profileData.medicalRecords.allergies.map((allergy: any) => (
                          <div key={allergy.id} className="bg-red-50 border border-red-200 p-3 rounded-lg">
                            <p className="font-medium text-red-900">{allergy.allergen}</p>
                            <p className="text-sm text-red-700">Severity: {allergy.severity}</p>
                            {allergy.reaction && <p className="text-sm text-gray-600 mt-1">{allergy.reaction}</p>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No allergies recorded</p>
                    )}
                  </div>

                  {/* Conditions */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Medical Conditions</h4>
                    {profileData.medicalRecords.conditions.length > 0 ? (
                      <div className="space-y-2">
                        {profileData.medicalRecords.conditions.map((condition: any) => (
                          <div key={condition.id} className="bg-yellow-50 border border-yellow-100 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{condition.name}</p>
                                <p className="text-sm text-gray-600">Diagnosed: {formatDate(condition.diagnosedDate)}</p>
                                {condition.notes && <p className="text-sm text-gray-500 mt-1">{condition.notes}</p>}
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                condition.status === 'active' ? 'bg-orange-100 text-orange-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {condition.status}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No conditions recorded</p>
                    )}
                  </div>

                  {/* Recent Vital Signs */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Recent Vital Signs</h4>
                    {profileData.medicalRecords.vitalSigns.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">BP</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Temp</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pulse</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">O2</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {profileData.medicalRecords.vitalSigns.map((vital: any) => (
                              <tr key={vital.id}>
                                <td className="px-3 py-2 text-sm text-gray-900">{formatDateTime(vital.recordedAt)}</td>
                                <td className="px-3 py-2 text-sm text-gray-600">{vital.bloodPressure || '-'}</td>
                                <td className="px-3 py-2 text-sm text-gray-600">{vital.temperature ? `${vital.temperature}°F` : '-'}</td>
                                <td className="px-3 py-2 text-sm text-gray-600">{vital.pulse ? `${vital.pulse} bpm` : '-'}</td>
                                <td className="px-3 py-2 text-sm text-gray-600">{vital.oxygenSaturation ? `${vital.oxygenSaturation}%` : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No vital signs recorded</p>
                    )}
                  </div>
                </div>
              )}

              {/* Assigned Staff Tab */}
              {activeTab === 'staff' && profileData && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Assigned Caregivers</h4>
                  {profileData.assignedStaff.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {profileData.assignedStaff.map((staff: any) => (
                        <div key={staff.id} className="bg-blue-50 border border-blue-100 p-4 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="h-12 w-12 rounded-full bg-primary-500 flex items-center justify-center">
                              <span className="text-lg font-medium text-white">
                                {staff.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{staff.name}</p>
                              <p className="text-sm text-gray-600">{staff.email}</p>
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full mt-1 inline-block">
                                {staff.role}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm">No staff assigned to this resident</p>
                  )}
                </div>
              )}

              {/* Activities Tab */}
              {activeTab === 'activities' && profileData && (
                <div className="space-y-4">
                  {/* Upcoming Appointments */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Upcoming Appointments</h4>
                    {profileData.upcomingAppointments.length > 0 ? (
                      <div className="space-y-2">
                        {profileData.upcomingAppointments.map((apt: any) => (
                          <div key={apt.id} className="bg-primary-50 border border-primary-100 p-3 rounded-lg">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{apt.title}</p>
                                <p className="text-sm text-gray-600">{formatDateTime(apt.appointmentDate)}</p>
                                {apt.location && <p className="text-sm text-gray-500">📍 {apt.location}</p>}
                              </div>
                              <span className="text-xs px-2 py-1 bg-primary-100 text-primary-800 rounded-full">
                                {apt.type}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No upcoming appointments</p>
                    )}
                  </div>

                  {/* Recent Notes */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Recent Notes</h4>
                    {profileData.recentNotes.length > 0 ? (
                      <div className="space-y-2">
                        {profileData.recentNotes.map((note: any) => (
                          <div key={note.id} className="bg-green-50 border border-green-100 p-3 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">{note.type}</span>
                              <span className="text-xs text-gray-500">{formatDateTime(note.createdAt)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{note.content}</p>
                            <p className="text-xs text-gray-500 mt-1">By: {note.authorName}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No recent notes</p>
                    )}
                  </div>

                  {/* Recent Incidents */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Recent Incidents</h4>
                    {profileData.recentIncidents.length > 0 ? (
                      <div className="space-y-2">
                        {profileData.recentIncidents.map((incident: any) => (
                          <div key={incident.id} className="bg-red-50 border border-red-200 p-3 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <p className="font-medium text-red-900">{incident.title}</p>
                              <span className={`text-xs px-2 py-1 rounded-full ${
                                incident.severity === 'high' ? 'bg-red-200 text-red-900' :
                                incident.severity === 'medium' ? 'bg-orange-200 text-orange-900' :
                                'bg-yellow-200 text-yellow-900'
                              }`}>
                                {incident.severity}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700">{incident.description}</p>
                            <p className="text-xs text-gray-500 mt-1">{formatDateTime(incident.incidentDate)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No recent incidents</p>
                    )}
                  </div>

                  {/* Active Support Plans */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Active Support Plans</h4>
                    {profileData.activeSupportPlans.length > 0 ? (
                      <div className="space-y-2">
                        {profileData.activeSupportPlans.map((plan: any) => (
                          <div key={plan.id} className="bg-indigo-50 border border-indigo-100 p-3 rounded-lg">
                            <p className="font-medium text-gray-900">{plan.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                            <div className="flex justify-between items-center mt-2">
                              <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full">{plan.category}</span>
                              <span className="text-xs text-gray-500">Review: {formatDate(plan.reviewDate)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No active support plans</p>
                    )}
                  </div>
                </div>
              )}

              {/* External Care Plans Tab */}
              {activeTab === 'external-care-plans' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="text-lg font-semibold text-gray-900">External Care Plans</h4>
                    <button
                      onClick={() => setShowUploadForm(!showUploadForm)}
                      className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <svg className="-ml-0.5 mr-1.5 h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Upload
                    </button>
                  </div>

                  {/* Upload Form */}
                  {showUploadForm && (
                    <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select File</label>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                        <input
                          type="text"
                          value={uploadDescription}
                          onChange={(e) => setUploadDescription(e.target.value)}
                          placeholder="Brief description of the document"
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
                        />
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleUploadExternalCarePlan}
                          disabled={uploading}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {uploading ? 'Uploading...' : 'Upload File'}
                        </button>
                        <button
                          onClick={() => {
                            setShowUploadForm(false);
                            setUploadDescription('');
                          }}
                          className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {ecpError && (
                    <div className="rounded-md bg-red-50 p-3">
                      <p className="text-sm text-red-700">{ecpError}</p>
                    </div>
                  )}

                  {ecpLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  ) : externalCarePlans.length > 0 ? (
                    <div className="space-y-2">
                      {externalCarePlans.map((plan) => (
                        <div key={plan.id} className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                          <div className="flex justify-between items-start">
                            <div className="flex items-start space-x-3">
                              <span className="text-2xl">{getFileTypeIcon(plan.fileType)}</span>
                              <div>
                                <a
                                  href={plan.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                  {plan.fileName}
                                </a>
                                {plan.description && (
                                  <p className="text-sm text-gray-600 mt-0.5">{plan.description}</p>
                                )}
                                <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500">
                                  {plan.uploadedBy && <span>Uploaded by: {plan.uploadedBy.name}</span>}
                                  <span>{formatDate(plan.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={() => handleDeleteExternalCarePlan(plan.id)}
                              className="text-red-400 hover:text-red-600 p-1"
                              title="Delete document"
                            >
                              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-8">No external care plans uploaded yet</p>
                  )}
                </div>
              )}

              {/* Finance Tab */}
              {activeTab === 'finance' && profileData && (
                <div className="space-y-4">
                  {profileData.financeAccount ? (
                    <>
                      <div className="bg-green-50 border border-green-200 p-6 rounded-lg">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Account Balance</h4>
                        <p className="text-3xl font-bold text-green-700">
                          £{Number(profileData.financeAccount.balance).toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Account opened: {formatDate(profileData.financeAccount.createdAt)}
                        </p>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Recent Transactions</h4>
                        {profileData.recentTransactions.length > 0 ? (
                          <div className="space-y-2">
                            {profileData.recentTransactions.map((transaction: any) => (
                              <div key={transaction.id} className="bg-gray-50 border border-gray-200 p-3 rounded-lg">
                                <div className="flex justify-between items-start">
                                  <div>
                                    <p className="font-medium text-gray-900">{transaction.type}</p>
                                    {transaction.category && (
                                      <p className="text-sm text-gray-600">{transaction.category}</p>
                                    )}
                                    {transaction.description && (
                                      <p className="text-sm text-gray-500 mt-1">{transaction.description}</p>
                                    )}
                                    <p className="text-xs text-gray-500 mt-1">{formatDateTime(transaction.createdAt)}</p>
                                  </div>
                                  <p className={`text-lg font-semibold ${
                                    transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                                  }`}>
                                    {transaction.type === 'deposit' ? '+' : '-'}£{Number(transaction.amount).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No recent transactions</p>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No finance account set up for this resident</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResidentDetailsModal;
